export function getProfileDisplayName(profile: any): string | undefined {
  return profile?.name;
}

export function getProfileCanonicalName(profile: any): string | undefined {
  return profile?.originalName ?? profile?.name;
}

export function applyDefaultProfileName<T>(
  profiles: T[] | undefined,
  defaultProfileName: string | undefined,
  getName: (profile: T) => string | undefined = (profile: T) => getProfileDisplayName(profile),
): T[] {
  return (profiles ?? []).map((profile) => ({
    ...profile,
    isDefault: !!defaultProfileName && getName(profile) === defaultProfileName,
  }));
}

export function resolveProfileName<T>(
  profiles: T[] | undefined,
  options: {
    selectedName?: string;
    defaultProfileName?: string;
    getName?: (profile: T) => string | undefined;
  } = {},
): string | undefined {
  const list = profiles ?? [];
  const getName = options.getName ?? ((profile: T) => getProfileDisplayName(profile));

  if (options.selectedName && list.some((profile) => getName(profile) === options.selectedName)) {
    return options.selectedName;
  }

  if (options.defaultProfileName) {
    const defaultProfile = list.find((profile: any) => {
      const profileName = getName(profile);
      return profileName === options.defaultProfileName || !!profile?.isDefault;
    });
    if (defaultProfile) {
      return getName(defaultProfile);
    }
  }

  return list.length > 0 ? getName(list[0]) : undefined;
}

export function resolveProfileNameByPriority<T>(
  profiles: T[] | undefined,
  candidateNames: (string | undefined)[],
  getName: (profile: T) => string | undefined = (profile: T) => getProfileDisplayName(profile),
): string | undefined {
  const list = profiles ?? [];

  for (const candidateName of candidateNames) {
    if (!candidateName) continue;
    if (list.some((profile) => getName(profile) === candidateName)) {
      return candidateName;
    }
  }

  return list.length > 0 ? getName(list[0]) : undefined;
}

export function resolveActiveProfileName<T>(
  profiles: T[] | undefined,
  options: {
    selectedName?: string;
    defaultProfileName?: string;
    getName?: (profile: T) => string | undefined;
  } = {},
): string | undefined {
  return resolveProfileNameByPriority(
    profiles,
    [options.selectedName, options.defaultProfileName],
    options.getName,
  );
}

export function resolveInitialProfileName<T>(
  profiles: T[] | undefined,
  options: {
    defaultProfileName?: string;
    fallbackProfileName?: string;
    getName?: (profile: T) => string | undefined;
  } = {},
): string | undefined {
  return resolveProfileNameByPriority(
    profiles,
    [options.defaultProfileName, options.fallbackProfileName],
    options.getName,
  );
}

export function resolveProfileNameByFieldPriority<T>(
  profiles: T[] | undefined,
  options: {
    field: keyof T;
    priority: string[];
    getName?: (profile: T) => string | undefined;
  },
): string | undefined {
  const list = profiles ?? [];
  const getName = options.getName ?? ((profile: T) => getProfileDisplayName(profile));

  const candidateNames = options.priority
    .map((priorityValue) =>
      list.find((profile: any) => String(profile?.[options.field] ?? '') === priorityValue),
    )
    .map((profile) => (profile ? getName(profile) : undefined));

  return resolveProfileNameByPriority(list, candidateNames, getName);
}
